import { useState } from 'react'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { groupChatService } from '@/services/groupChatService'
import { toast } from 'sonner'
import { Copy, Link as LinkIcon, Loader2, Trash2 } from 'lucide-react'
import { GroupInvitation, GenerateInviteRequest } from '@/types/groupChat'
import { format } from 'date-fns'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

interface InviteManagerProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    conversationId: string
}

/**
 * InviteManager Component
 * Manages group chat invitations - generate, view, and revoke invites
 */
export default function InviteManager({
    open,
    onOpenChange,
    conversationId,
}: InviteManagerProps) {
    const { t } = useTranslation('common')
    const queryClient = useQueryClient()
    const [expiresAt, setExpiresAt] = useState('')
    const [maxUses, setMaxUses] = useState('')
    const [isGenerating, setIsGenerating] = useState(false)

    // Fetch active invites
    const { data: invites = [], isLoading } = useQuery({
        queryKey: ['groupInvites', conversationId],
        queryFn: () => groupChatService.getActiveInvites(conversationId),
        enabled: open && !!conversationId,
    })

    // Generate invite mutation
    const generateInviteMutation = useMutation({
        mutationFn: () => {
            const requestData: GenerateInviteRequest = {
                conversationId // Add conversationId to body as per spec (optional but might be required by backend validation)
            }

            // Only add expiresAt if it has a value
            if (expiresAt && expiresAt.trim()) {
                // Convert datetime-local format to ISO 8601
                const date = new Date(expiresAt)
                requestData.expiresAt = date.toISOString()
            }

            // Only add maxUses if it has a value
            if (maxUses && maxUses.trim()) {
                const parsedMaxUses = parseInt(maxUses, 10)
                if (!isNaN(parsedMaxUses) && parsedMaxUses > 0) {
                    requestData.maxUses = parsedMaxUses
                }
            }

            // console.log('Generating invite with data:', requestData)
            return groupChatService.generateInvite(conversationId, requestData)
        },
        onSuccess: (data) => {
            toast.success(t('chat.inviteManager.inviteLinkGenerated'), {
                description: t('chat.inviteManager.copy'),
                action: {
                    label: t('chat.inviteManager.copy'),
                    onClick: () => {
                        const inviteLink = `${window.location.origin}/chat/join/${data.inviteCode}`
                        navigator.clipboard.writeText(inviteLink)
                        toast.success(t('chat.inviteManager.inviteLinkCopied'))
                    },
                },
            })

            // Reset form
            setExpiresAt('')
            setMaxUses('')

            // Refresh invites list
            queryClient.invalidateQueries({ queryKey: ['groupInvites', conversationId] })
        },
        onError: (error: any) => {
            toast.error(
                error.response?.data?.message || t('chat.inviteManager.errors.generateFailed')
            )
        },
    })

    // Revoke invite mutation
    const revokeInviteMutation = useMutation({
        mutationFn: (inviteCode: string) =>
            groupChatService.revokeInvite(inviteCode),
        onSuccess: () => {
            toast.success(t('chat.inviteManager.inviteLinkRevoked'))
            queryClient.invalidateQueries({ queryKey: ['groupInvites', conversationId] })
        },
        onError: (error: any) => {
            toast.error(
                error.response?.data?.message || t('chat.inviteManager.errors.revokeFailed')
            )
        },
    })

    const handleGenerate = async () => {
        setIsGenerating(true)
        try {
            await generateInviteMutation.mutateAsync()
        } finally {
            setIsGenerating(false)
        }
    }

    const copyInviteLink = (invite: GroupInvitation) => {
        const inviteLink = `${window.location.origin}/chat/join/${invite.inviteCode}`
        navigator.clipboard.writeText(inviteLink)
        toast.success(t('chat.inviteManager.inviteLinkCopied'))
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                    <DialogTitle>{t('chat.inviteManager.title')}</DialogTitle>
                    <DialogDescription>
                        {t('chat.inviteManager.description')}
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-6">
                    {/* Generate new invite */}
                    <div className="space-y-4">
                        <h4 className="font-medium text-sm">{t('chat.inviteManager.generateNew')}</h4>

                        <div className="grid gap-4">
                            {/* Expiration date */}
                            <div className="grid gap-2">
                                <Label htmlFor="expiresAt">{t('chat.inviteManager.expirationDate')}</Label>
                                <Input
                                    id="expiresAt"
                                    type="datetime-local"
                                    value={expiresAt}
                                    onChange={(e) => setExpiresAt(e.target.value)}
                                    disabled={isGenerating}
                                />
                            </div>

                            {/* Max uses */}
                            <div className="grid gap-2">
                                <Label htmlFor="maxUses">{t('chat.inviteManager.maxUses')}</Label>
                                <Input
                                    id="maxUses"
                                    type="number"
                                    min="1"
                                    placeholder={t('chat.inviteManager.maxUsesPlaceholder')}
                                    value={maxUses}
                                    onChange={(e) => setMaxUses(e.target.value)}
                                    disabled={isGenerating}
                                />
                            </div>

                            <Button onClick={handleGenerate} disabled={isGenerating}>
                                {isGenerating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                <LinkIcon className="mr-2 h-4 w-4" />
                                {t('chat.inviteManager.generateButton')}
                            </Button>
                        </div>
                    </div>

                    {/* Active invites list */}
                    <div className="space-y-4">
                        <h4 className="font-medium text-sm">{t('chat.inviteManager.activeInvites')}</h4>

                        {isLoading ? (
                            <div className="flex items-center justify-center py-8">
                                <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                            </div>
                        ) : invites.length > 0 ? (
                            <div className="space-y-2 max-h-[300px] overflow-y-auto">
                                {invites.map((invite) => (
                                    <div
                                        key={invite.id}
                                        className="flex items-center justify-between p-3 border border-gray-200 rounded-lg"
                                    >
                                        <div className="flex-1 min-w-0 mr-2">
                                            <div className="flex items-center gap-2 mb-1">
                                                <code className="text-xs bg-gray-100 px-2 py-1 rounded">
                                                    {invite.inviteCode}
                                                </code>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="h-6 w-6 p-0"
                                                    onClick={() => copyInviteLink(invite)}
                                                >
                                                    <Copy className="h-3 w-3" />
                                                </Button>
                                            </div>

                                            <div className="text-xs text-gray-500 space-y-1">
                                                {invite.expiresAt && (
                                                    <p>
                                                        {t('chat.inviteManager.expires')}:{' '}
                                                        {format(new Date(invite.expiresAt), 'PPp')}
                                                    </p>
                                                )}
                                                {invite.maxUses && (
                                                    <p>
                                                        {t('chat.inviteManager.uses')}: {invite.currentUses} / {invite.maxUses}
                                                    </p>
                                                )}
                                                {!invite.maxUses && (
                                                    <p>{t('chat.inviteManager.uses')}: {invite.currentUses}</p>
                                                )}
                                                <p>
                                                    {t('chat.inviteManager.createdBy')}: {invite.creator.firstname}{' '}
                                                    {invite.creator.lastname}
                                                </p>
                                            </div>
                                        </div>

                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                            onClick={() => revokeInviteMutation.mutate(invite.inviteCode)}
                                            disabled={revokeInviteMutation.isPending}
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </Button>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="text-sm text-gray-500 text-center py-8">
                                {t('chat.inviteManager.noActiveInvites')}
                            </p>
                        )}
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    )
}
